/**
 * TutorialContext — client mirror of server-side TutorialState (PRD FR-1..FR-6).
 *
 * Contract with Felix's backend (player service; final prefixes his call — the
 * client reads them from one map so a rename is a one-line change):
 *   GET    /tutorial/state      -> TutorialState (null/404 = not created yet)
 *   POST   /tutorial/start
 *   POST   /tutorial/ack        { step_id }
 *   POST   /tutorial/skip       { step_id }
 *   POST   /tutorial/restart
 * Subscribes to tutorial.* WebSocket events; server is authoritative on
 * milestones — the client NEVER completes a milestone step from UI clicks
 * (FR-2). Degraded mode: any API failure => tutorial UI fully hidden, game
 * fully playable (NFR: tutorial is a layer, not a gate).
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import api from '../services/api'
import websocketService from '../services/websocket'
import { useAuth } from './AuthContext'
import { resolveTargetRect } from '../utils/tutorialTargets'
import { TUTORIAL_STEPS, TOTAL_STEPS, MILESTONE_TO_STEP, getStep } from '../constants/tutorialContent'

const TutorialContext = createContext(null)

// Endpoints — single map so backend renames stay a one-line change.
// stepComplete takes { stepId } and is expanded into the path segment by post().
export const TUTORIAL_ENDPOINTS = {
    state: '/player/tutorial',
    start: '/player/tutorial/start',
    stepComplete: '/player/tutorial/step-complete', // + `/${stepId}` (path param)
    skip: '/player/tutorial/skip',
    restart: '/player/tutorial/restart',
}

const POLL_MS = 30000

// Welcome dismissal (NOT skip) — per-user so a shared browser doesn't leak one
// account's "ignore it" choice to another. Cleared by Begin/Skip and by the
// character-reset flow (clearWelcomeDismissal from AuthenticatedLayout).
const dismissalKey = (userId) => `tcd_welcome_dismissed:${userId || 'anon'}`

export function clearWelcomeDismissal(userId) {
    try { localStorage.removeItem(dismissalKey(userId)) } catch { /* private mode */ }
}

export function TutorialProvider({ children }) {
    const { isAuthenticated, currentUser } = useAuth()
    const [state, setState] = useState(null)        // server TutorialState or null
    const [available, setAvailable] = useState(true) // false => degraded mode
    const [uiMode, setUiMode] = useState('hidden')   // hidden|welcome|spotlight|waiting|collapsed|completion
    const [beatIndex, setBeatIndex] = useState(0)
    const [loading, setLoading] = useState(false)
    const pollRef = useRef(null)
    const stateRef = useRef(state)
    stateRef.current = state
    const uiModeRef = useRef(uiMode)
    uiModeRef.current = uiMode
    const beatIndexRef = useRef(beatIndex)
    beatIndexRef.current = beatIndex
    // Set when the tutorial's own CTA navigates to another view (NOT when the
    // player clicks out) — the spotlight must return by itself once the target
    // view mounts, without the player hunting for the resume button.
    const navYieldRef = useRef(false)

    /** Auto-open the welcome once, when the server says not_started (US-1).
     *  Flag off (enabled === false) must behave exactly like "no tutorial".
     *  A previous dismissal (outside click / X / Esc) keeps the welcome tucked
     *  into the floating button instead of re-prompting every session. */
    const autoOpenRef = useRef(false)
    useEffect(() => {
        if (!isAuthenticated) { autoOpenRef.current = false; return }
        if (state && state.enabled !== false && state.status === 'not_started' && !autoOpenRef.current) {
            autoOpenRef.current = true
            let dismissed = false
            try { dismissed = localStorage.getItem(dismissalKey(currentUser?.id)) !== null } catch { /* private mode */ }
            setUiMode(dismissed ? 'collapsed' : 'welcome')
        }
    }, [isAuthenticated, state, currentUser])



    const resetUi = useCallback(() => {
        setUiMode('hidden')
        setBeatIndex(0)
    }, [])

    /** Derive the overlay mode from server state. */
    const deriveMode = useCallback((s) => {
        if (!s) return 'hidden'
        if (s.status === 'completed') return 'hidden' // completion handled transiently via WS event
        if (s.status === 'skipped') return 'hidden'
        if (s.status === 'not_started') return 'hidden' // welcome shows after explicit start
        if (s.status === 'active') {
            const step = getStep(s.current_step)
            if (!step) return 'hidden'
            // NOTE: current_step === TOTAL_STEPS on the LAST milestone step is
            // still in progress (e.g. step 8 waits for a vendor purchase) —
            // only status === 'completed' means done, and completion is shown
            // transiently via the WS event, never derived here.
            return step.kind === 'welcome' ? 'welcome' : 'spotlight'
        }
        return 'hidden'
    }, [])
    /** Intro steps completed server-side while the welcome is up: follow the
     *  server step (welcome -> spotlight). Never touches collapsed modes. */
    useEffect(() => {
        if (state && state.status === 'active' && uiModeRef.current === 'welcome') {
            const m = deriveMode(state)
            if (m !== 'welcome') setUiMode(m)
        }
    }, [state, deriveMode])


    /** Pull server state; tolerate 404 (not created yet) and any failure (degraded). */
    const refresh = useCallback(async () => {
        if (!localStorage.getItem('access_token')) return
        try {
            setLoading(true)
            const res = await api.get(TUTORIAL_ENDPOINTS.state)
            // Non-object body (SPA fallback HTML / string) = route not mounted
            // yet — same as a contract mismatch: log loudly, degrade cleanly.
            if (res.data && typeof res.data !== 'object') {
                console.error('[tutorial] non-JSON state body — backend route missing?', TUTORIAL_ENDPOINTS.state)
                setAvailable(false)
                setState(null)
            } else {
                setState(res.data || null)
                setAvailable(true)
                // FR-5 session resume: a mid-tutorial re-login starts at the same
                // step. Only when the UI is hidden (fresh mount), never clobbering
                // collapsed/waiting/completion modes the user is inside.
                if (uiModeRef.current === 'hidden') {
                    setUiMode(deriveMode(res.data || null))
                }
            }
        } catch (err) {
            const status = err.response?.status
            if (status === 404) {
                setState(null) // not created yet — not degraded
                setAvailable(true)
            } else if (status === 401 || status === 403) {
                setState(null) // session expired mid-poll — wait for re-auth, not degraded
                setAvailable(true)
            } else if (status === 422 || status === 400) {
                // Schema mismatch with the backend contract during integration —
                // log loudly but keep the tutorial OUT of the way of gameplay.
                console.error('[tutorial] contract mismatch on', TUTORIAL_ENDPOINTS.state, err.response?.data)
                setAvailable(false)
                setState(null)
            } else {
                setAvailable(false)
                setState(null)
            }
        } finally {
            setLoading(false)
        }
    }, [deriveMode])

    /** Fire-and-forget POST that never blocks gameplay. */
    const post = useCallback(async (path, body) => {
        try {
            const res = await api.post(path, body || {})
            if (res.data) setState(res.data)
            setAvailable(true)
            return res.data
        } catch {
            setAvailable(false)
            return null
        }
    }, [])

    // ---- initial fetch + poll -------------------------------------------------
    useEffect(() => {
        if (!isAuthenticated) {
            setState(null)
            setAvailable(true)
            resetUi()
            return undefined
        }
        refresh()
        pollRef.current = setInterval(refresh, POLL_MS)
        return () => clearInterval(pollRef.current)
    }, [isAuthenticated, refresh, resetUi])

    // ---- WebSocket: server is authoritative -----------------------------------
    useEffect(() => {
        if (!isAuthenticated) return undefined
        const handlers = [
            websocketService.on('tutorial.started', () => {}),
            websocketService.on('tutorial.step.completed', (e) => {
                // Optimistic merge from the event payload; next poll reconciles.
                const cur = stateRef.current
                if (!cur) return
                const stepId = e?.step_id ?? (e?.milestone ? MILESTONE_TO_STEP[e.milestone] : null)
                if (!stepId) return
                setState((prev) => (prev && prev.current_step === stepId
                    ? { ...prev, current_step: Math.min(prev.current_step + 1, TOTAL_STEPS) }
                    : prev))
                setBeatIndex(0)
                // Milestone landed while pill-waiting -> bring the spotlight back.
                setUiMode((mode) => (mode === 'waiting' || mode === 'collapsed' ? 'spotlight' : mode))
            }),
            websocketService.on('tutorial.skipped', () => {
                // Server is authoritative on skip; mirror it immediately.
                resetUi()
            }),
            websocketService.on('tutorial.restarted', (e) => {
                const resumeStep = e?.resume_step
                setBeatIndex(0)
                refresh().then((s) => {
                    if (resumeStep && s) setState({ ...s, current_step: resumeStep })
                    const cur = stateRef.current
                    if (cur) setUiMode(deriveMode(cur) === 'hidden' ? 'spotlight' : deriveMode(cur))
                })
            }),
            websocketService.on('tutorial.completed', () => {
                setUiMode('completion')
                setBeatIndex(0)
            },
            ),
        ]
        return () => handlers.forEach((off) => off && off())
    }, [isAuthenticated, refresh, deriveMode, resetUi])

    // ---- actions ---------------------------------------------------------------
    const start = useCallback(async () => {
        // Begin = dismiss the intro (PRD: tutorial.started fires on first
        // dismiss of step 1). The server moves to step 2; show its spotlight.
        // (deriveMode alone would re-derive 'welcome' while current_step is
        // still 1 — the welcome must never reshow after Begin.)
        const next = await post(TUTORIAL_ENDPOINTS.start)
        if (next) {
            clearWelcomeDismissal(currentUser?.id)
            setBeatIndex(0)
            const mode = deriveMode(next)
            const step = getStep(next.current_step)
            setUiMode(mode === 'welcome' || (step && step.kind === 'welcome') ? 'spotlight' : mode)
        }
        return next
    }, [post, deriveMode, currentUser])

    const ackStep = useCallback(async (stepId) => {
        // Intro steps 1-2 ONLY (FR-2): milestone steps can never be acked from
        // the UI — the backend rejects them and the client must not pretend.
        if (stepId !== 1 && stepId !== 2) return stateRef.current
        const next = await post(`${TUTORIAL_ENDPOINTS.stepComplete}/${stepId}`)
        return next
    }, [post])

    const skip = useCallback(async () => {
        // Backend records skipped_at_step itself — no body needed.
        const next = await post(TUTORIAL_ENDPOINTS.skip)
        clearWelcomeDismissal(currentUser?.id)
        resetUi()
        return next
    }, [post, resetUi, currentUser])

    const restart = useCallback(async () => {
        const next = await post(TUTORIAL_ENDPOINTS.restart)
        if (next) {
            setBeatIndex(0)
            const mode = deriveMode(next)
            setUiMode(mode === 'hidden' ? 'spotlight' : mode)
        }
        return next
    }, [post, deriveMode])

    const collapse = useCallback(() => {
        try { localStorage.setItem(dismissalKey(currentUser?.id), new Date().toISOString()) } catch { /* private mode */ }
        setUiMode('collapsed')
    }, [currentUser])

    // Navigation yield: like collapse, but never records a dismissal and arms
    // the auto-resume watcher below.
    const yieldForNavigation = useCallback(() => {
        navYieldRef.current = true
        setUiMode('collapsed')
    }, [])

    // Auto-resume after a navigation yield: poll until the current beat's
    // target element is on screen (lazy view mounted), then bring the
    // spotlight back. Any other mode change (manual resume, action started,
    // skip) cancels the watcher.
    useEffect(() => {
        if (uiMode !== 'collapsed') { navYieldRef.current = false; return undefined }
        if (!navYieldRef.current) return undefined
        const s = stateRef.current
        const step = s && s.status === 'active' ? getStep(s.current_step) : null
        if (!step?.beats?.length) { navYieldRef.current = false; return undefined }
        const iv = setInterval(() => {
            const beat = step.beats[Math.min(beatIndexRef.current, step.beats.length - 1)]
            if (!beat?.target) return
            if (resolveTargetRect(beat.target).ok) {
                navYieldRef.current = false
                setUiMode('spotlight')
            }
        }, 250)
        return () => clearInterval(iv)
    }, [uiMode, state, beatIndex])
    const resume = useCallback(() => {
        const s = stateRef.current
        if (!s) { resetUi(); return }
        // Pre-Begin players: deriveMode maps not_started to 'hidden' (welcome is
        // auto-open-once), so the old spotlight fallback rendered nothing and the
        // player had no way back in without a refresh. Re-present the welcome.
        if (s.status === 'not_started') { setUiMode('welcome'); return }
        const mode = deriveMode(s)
        if (mode === 'hidden') {
            setUiMode(getStep(s.current_step) ? 'spotlight' : 'welcome')
            return
        }
        setUiMode(mode)
        // Beat position inside the step is preserved: collapse/resume must not
        // replay beats the player already advanced past (navigate CTAs yield
        // to the pill mid-beat-sequence and come straight back).
    }, [deriveMode, resetUi])

    const nextBeat = useCallback(() => setBeatIndex((i) => i + 1), [])
    const setBeat = useCallback((i) => setBeatIndex(i), [])

    const dismissCompletion = useCallback(() => resetUi(), [resetUi])

    const value = useMemo(() => ({
        state,
        available,
        loading,
        uiMode,
        beatIndex,
        currentStep: state ? getStep(state.current_step) : null,
        steps: TUTORIAL_STEPS,
        totalSteps: TOTAL_STEPS,
        currentUser,
        start,
        ackStep,
        skip,
        restart,
        collapse,
        resume,
        yieldForNavigation,
        nextBeat,
        setBeat,
        dismissCompletion,
        refresh,
    }), [state, available, loading, uiMode, beatIndex, currentUser, start, ackStep, skip,
        restart, collapse, resume, yieldForNavigation, nextBeat, setBeat, dismissCompletion, refresh])

    return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>
}

export function useTutorial() {
    const ctx = useContext(TutorialContext)
    if (!ctx) throw new Error('useTutorial must be used within a TutorialProvider')
    return ctx
}

export default TutorialContext
